import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { IconLoading } from "../../assets/icons";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useGoogleLogin } from "@react-oauth/google";
import useAuthedApi from "../../hooks/useAuthedApi";
import { extractErrorMessage } from "../../utils";
import DialogVerifyRegister from "../dialogs/DialogVerifyRegister";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { useToast } from "../../context/toastContext";

// Detects the specific deactivated/deleted error message from the server
const isDeactivatedError = (data) => {
  if (!data) return false;
  const msg =
    typeof data === "string"
      ? data
      : data?.detail || data?.message || data?.error || JSON.stringify(data);
  return /deactivated|deleted|re-register|reactivate/i.test(msg);
};

export const LoginForm = () => {
  const { addToast } = useToast();
  const { authedPost } = useAuthedApi();
  const location = useLocation();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { fetchUser, user, isLoading } = useAuth();

  // Single loading flag covers check-email + login in sequence
  const [loading, setLoading] = useState(false);
  const [isVerificationOpen, setVerificationOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [isLoginVerification, setIsLoginVerification] = useState(false);

  // When true: password/forgot/remember hidden; banner + reactivate button shown
  const [isAccountInactive, setIsAccountInactive] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    // Reset deactivated state whenever user edits the email field
    if (id === "email") {
      setIsAccountInactive(false);
      setError("");
    }
  };

  const handleReactivate = () => {
    navigate("/signup", {
      state: { reactivate: true, email: formData.email },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Already confirmed inactive — just redirect, skip API calls
    if (isAccountInactive) {
      handleReactivate();
      return;
    }

    setLoading(true);
    setError("");

    // ── Step 1: check-email ─────────────────────────────────────────────────
    try {
      const checkRes = await authedPost(`/api/check-email/`, {
        email: formData.email,
      });
      const checkData = checkRes.data;

      if (
        checkData?.is_active === false ||
        checkData?.inactive ||
        isDeactivatedError(checkData)
      ) {
        setIsAccountInactive(true);
        setError(
          "This account has been deactivated/deleted. Please re-register to reactivate it.",
        );
        setLoading(false);
        return;
      }
    } catch (checkErr) {
      const status = checkErr.response?.status;
      const checkData = checkErr.response?.data;

      // ONLY treat as inactive for intentional auth/account responses
      const shouldReactivate =
        status !== 500 &&
        (checkData?.is_active === false ||
          checkData?.inactive ||
          checkData?.account_inactive ||
          isDeactivatedError(checkData));

      if (shouldReactivate) {
        setIsAccountInactive(true);
        setError(
          "This account has been deactivated/deleted. Please re-register to reactivate it.",
        );
        setLoading(false);
        return;
      }

      // For server errors/network errors → show generic error
      if (status >= 500) {
        setError("Server error. Please try again in a few moments.");
        setLoading(false);
        return;
      }
      // Any other check-email error (network, etc.) → don't block, fall through to login
    }

    // ── Step 2: login ──────────────────────────────────────────────────────
    try {
      const response = await authedPost(`/api/login/`, {
        email: formData.email,
        password: formData.password,
      });

      localStorage.removeItem("verification_code_signup");
      localStorage.removeItem("verification_code_signin");

      if (response.data?.requires_verification && response.data?.token) {
        setIsLoginVerification(true);
        setUserId(response.data.token);
        setVerificationOpen(true);
        return;
      }

      if (response.status === 200) {
        await fetchUser();
        navigate("/dashboard");
      }
    } catch (loginErr) {
      const data = loginErr.response?.data;

      // Deactivated surfaced from login endpoint (fallback safety net)
      if (
        data?.is_active === false ||
        data?.inactive ||
        data?.account_inactive ||
        isDeactivatedError(data)
      ) {
        setIsAccountInactive(true);
        setError(
          "This account has been deactivated/deleted. Please re-register to reactivate it.",
        );
        return;
      }

      // Unverified account
      const token = data?.verification_token;
      if (token) {
        setVerificationOpen(true);
        setUserId(token);
        setError(
          "Please verify your account. We've resent the verification email.",
        );
        try {
          await authedPost(`/api/verify/resend`, {
            email: formData.email,
            verification_token: token,
          });
        } catch (resendErr) {
          console.error("Failed to resend verification email:", resendErr);
        }
        return;
      }

      console.error("Login error:", data || loginErr.message);
      setError(
        extractErrorMessage(
          data || "Invalid email or password. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const googleLoginFn = useGoogleLogin({
    flow: "auth-code",
    prompt: "consent",
    access_type: "offline",
    redirect_uri: "https://stg2.xenorize.com",
    scope: "openid email profile",
    onSuccess: async (tokenResponse) => {
      if (loading) return;
      setLoading(true);
      setError("");
      try {
        const response = await authedPost(`/api/googlelogin/`, {
          credential: tokenResponse.code,
        });
        if (response.status === 200) {
          await fetchUser();
          navigate("/dashboard");
        }
      } catch (error) {
        console.error(
          "Google login error:",
          error.response?.data || error.message,
        );
        setError("An error occurred during Google login. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google Login hook error:", error);
      setError("Failed to initiate Google login.");
    },
  });

  // Show toast carried over from a previous page (e.g. after account deactivation)
  useEffect(() => {
    const raw = localStorage.getItem("pending_toast");
    if (raw) {
      try {
        const toast = JSON.parse(raw);
        addToast(toast.type, toast.message, 6000);
      } catch (_) {}
      localStorage.removeItem("pending_toast");
    }
  }, []);

  if (!isLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      className="min-h-[100dvh] w-full flex items-center justify-center"
      style={{
        background: "linear-gradient(to bottom left, #413072, #08080B)",
      }}
    >
      <DialogVerifyRegister
        isVerificationOpen={isVerificationOpen}
        setVerificationOpen={setVerificationOpen}
        userId={userId}
        email={formData.email}
        isLoginVerification={isLoginVerification}
        isLogin
      />

      <div className="flex items-center m-8 w-full md:w-[70%] h-fit max-w-6xl bg-white rounded-xl overflow-hidden shadow-lg">
        <div className="w-full p-6 md:p-8">
          <form onSubmit={handleSubmit} className="w-full">
            <h1 className="text-2xl lg:text-4xl font-bold mb-0 text-left">
              Get Started
            </h1>
            <h4 className="text-sm lg:text-base font-bold text-left text-[#7D7A7A] mb-7">
              Enter your personal details below to continue
            </h4>

            {/* ── Deactivated account banner ── */}
            {isAccountInactive && (
              <div className="bg-[rgba(255,204,0,0.15)] gap-3 p-3 mb-4 rounded-xl border border-[#FFCC00] flex items-start">
                <ExclamationTriangleIcon className="w-full max-w-[20px] h-[20px] text-[#8F7303] flex-shrink-0" />
                <div>
                  <p className="font-bold leading-[0] !mb-[1rem] block">
                    Account Deactivated
                  </p>
                  <p>
                    This account has been deactivated or deleted. Click{" "}
                    <strong>Reactivate Your Account</strong> below to
                    re-register and restore access.
                  </p>
                </div>
              </div>
            )}

            {/* Email Field — always visible */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm mb-1">
                Email
              </label>
              <input
                id="email"
                type="text"
                placeholder="Enter your email"
                onChange={handleChange}
                required
                value={formData.email}
                className="mt-[2px]"
              />
            </div>

            {/* Password + Remember/Forgot — hidden when account is deactivated */}
            {!isAccountInactive && (
              <>
                <div className="mb-4">
                  <label htmlFor="password" className="block text-sm mb-1">
                    Password
                  </label>
                  <div className="relative -mt-2">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      onChange={handleChange}
                      required
                      value={formData.password}
                      className="!pr-[2.5rem]"
                    />
                    {showPassword ? (
                      <EyeSlashIcon
                        className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    ) : (
                      <EyeIcon
                        className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm mb-6">
                  <label className="flex items-center justify-center space-x-2">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">
                      Keep me logged in
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-blue-600 hover:underline text-xs"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </>
            )}

            {/* Generic errors (not deactivated) */}
            {error && !isAccountInactive && (
              <p className="text-red-600 text-xs mb-2">{error}</p>
            )}

            {/* ── Primary CTA ── */}
            {isAccountInactive ? (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleReactivate}
                  className="w-full !py-3 !font-bold !text-base !bg-amber-500 hover:!bg-amber-600 text-white rounded-lg transition"
                >
                  Reactivate Your Account
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountInactive(false);
                    setError("");
                    setFormData({ email: "", password: "" });
                  }}
                  className="w-full !py-3 !font-bold !text-base !bg-white border border-gray-300 !text-gray-600 hover:!bg-gray-50 rounded-lg transition"
                >
                  Use Another Account
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="w-full relative overflow-hidden !py-3 !font-bold !text-base !bg-[#007A78]"
              >
                {loading ? (
                  <div className="animate-spin w-fit flex justify-center items-center mx-auto">
                    <IconLoading className="w-[24px] h-[24px]" />
                  </div>
                ) : (
                  "Login"
                )}
              </button>
            )}

            {/* Divider */}
            <div className="flex items-center my-5 text-sm text-gray-500">
              <hr className="flex-1 border-gray-300" />
              <span className="mx-4">OR</span>
              <hr className="flex-1 border-gray-300" />
            </div>

            {/* Social Buttons */}
            <div className="flex flex-col md:flex-row gap-3">
              <a
                type="button"
                className="flex-1 border border-[#B7BABD] text-black bg-white px-1 py-3 rounded text-sm hover:bg-blue-50 transition text-center cursor-pointer rounded-lg flex items-center justify-center"
                onClick={googleLoginFn}
              >
                <img
                  alt="Google icon"
                  src="/images/icons/icon_google.png"
                  className="w-6 h-6 mr-1"
                />{" "}
                Google
              </a>
            </div>

            {/* Sign Up Link */}
            <div className="mt-6 text-sm text-center text-[#302571]">
              <Link to="/signup" className="hover:underline text-[#302571]">
                No account yet? Sign Up
              </Link>
            </div>
          </form>
        </div>

        {/* Right Image */}
        <div className="hidden w-full h-full xl:block p-5">
          <img
            src="/images/images_login.png"
            alt="Hero Banner Desktop"
            className="w-full h-full object-cover rounded-xl max-h-[calc(90vh-5rem)]"
          />
        </div>
      </div>
    </div>
  );
};
